/**
 * ListFilterBar.tsx — the filters an Admin list shows across its top, beside
 * the search box (place it inside AdminListToolbar).
 *
 * Each filter is Prospera's SearchableMultiSelect — the same control the
 * corporate dashboard's lists use for their filters, and the one Admin >
 * Users already filters with — so the corp filter rows carry over one for
 * one: a placeholder such as "All categories" while nothing is picked, "N
 * selected" (highlighted blue) once something is, a search box, Select All,
 * and an x that clears it. Nothing picked means no filtering.
 *
 * Every filter gets a visually hidden <label> (e.g. "Filter by category") so
 * screen readers — and tests — can find it by name.
 *
 * Filters only read, so they are never field-locked: they keep working on a
 * Locked Admin page, wherever they are placed.
 */
import { cn } from '@/lib/utils';
import { FieldLockScope } from '@/components/ui/FieldLock';
import { SearchableMultiSelect, type SearchableMultiSelectOption } from '@/components/ui/SearchableMultiSelect';

export interface ListFilterConfig {
  /** The trigger's id; also what the hidden label points at. */
  id: string;
  /** Accessible name, e.g. "Filter by category". */
  label: string;
  /** Shown while nothing is picked, e.g. "All categories". */
  placeholder: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** Values are strings (numeric ids as strings, never names, unless the filter is by name). */
  options: SearchableMultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  /** Tailwind width class for the control; defaults to w-48 (the corp width). */
  widthClass?: string;
}

/** True when any filter has something picked. */
export function anyFilterActive(filters: ReadonlyArray<{ selected: readonly string[] }>): boolean {
  return filters.some((f) => f.selected.length > 0);
}

/** The filter controls, inline — they flow in the toolbar's filter row. */
export function ListFilterBar({ filters }: { filters: ListFilterConfig[] }) {
  return (
    <FieldLockScope locked={false}>
      {filters.map((f) => (
        <div key={f.id} className={cn(f.widthClass ?? 'w-48', 'min-w-0 max-md:w-full')}>
          <label htmlFor={f.id} className="sr-only">
            {f.label}
          </label>
          <SearchableMultiSelect
            id={f.id}
            options={f.options}
            selectedValues={f.selected}
            onSelectionChange={f.onChange}
            placeholder={f.placeholder}
            searchPlaceholder={f.searchPlaceholder ?? 'Search…'}
            emptyMessage={f.emptyMessage}
            activeHighlight
          />
        </div>
      ))}
    </FieldLockScope>
  );
}
